import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { Aviso } from "../../../../../store/admin/avisosStore";
import ImagePreviewModal from "../../../../../components/base/ImagePreviewModal";
import type { ThemeClasses } from "./types";
import { DENSE_ROW, ICON_BTN, ICON_BTN_DANGER, STATUS_CHIP } from "../../../shared";

interface AvisoListRowProps {
  aviso: Aviso;
  isDarkMode: boolean;
  themeClasses: ThemeClasses;
  companyName?: string;
  isToggling?: boolean;
  onViewDetails: (aviso: Aviso) => void;
  onEdit: (aviso: Aviso) => void;
  onToggleStatus: (aviso: Aviso) => void;
  onDelete: (aviso: Aviso) => void;
}

export default function AvisoListRow({
  aviso,
  isDarkMode,
  themeClasses,
  companyName,
  isToggling = false,
  onViewDetails,
  onEdit,
  onToggleStatus,
  onDelete,
}: AvisoListRowProps) {
  const { t } = useTranslation("adminSections");
  const [previewOpen, setPreviewOpen] = useState(false);

  const readedCount = Array.isArray(aviso.readed_by)
    ? aviso.readed_by.length
    : 0;

  const fallbackTitle = t("avisos.title");
  const scopeCompanyName = companyName || aviso.company_id || "";

  return (
    <div className={`${DENSE_ROW} ${themeClasses.cardBg} ${themeClasses.cardBorder}`}>
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {aviso.image ? (
            <button
              type="button"
              onClick={() => setPreviewOpen(true)}
              className="relative group w-9 h-9 rounded-md overflow-hidden flex-shrink-0 focus:outline-none"
              aria-label={t("avisos.form.enlargeImage")}
            >
              <img
                src={aviso.image}
                alt={aviso.title || fallbackTitle}
                className="w-9 h-9 object-cover"
              />
              <span className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/40 transition-colors">
                <i className="ri-zoom-in-line text-white text-base opacity-0 group-hover:opacity-100 transition-opacity"></i>
              </span>
            </button>
          ) : (
            <div
              className={`w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0 ${
                isDarkMode ? "bg-gray-800" : "bg-gray-100"
              }`}
            >
              <i
                className={`ri-megaphone-line text-base ${themeClasses.textSecondary}`}
              ></i>
            </div>
          )}

          <div className="min-w-0 flex-1">
            <h3
              className={`font-semibold ${themeClasses.text} text-sm truncate`}
              title={aviso.title || t("avisos.noTitle")}
            >
              {aviso.title || t("avisos.noTitle")}
            </h3>
            <div className="flex items-center gap-2 text-xs min-w-0 overflow-hidden whitespace-nowrap">
              {aviso.company_id ? (
                <span
                  className="shrink-0 max-w-[12rem] truncate px-2 py-0.5 rounded-full font-medium text-blue-500 bg-blue-500/10"
                  title={t("avisos.scopeCompanyLabel", {
                    name: scopeCompanyName,
                  })}
                >
                  <i className="ri-building-line mr-1"></i>
                  {companyName || aviso.company_id}
                </span>
              ) : (
                <span
                  className="shrink-0 px-2 py-0.5 rounded-full font-medium text-purple-500 bg-purple-500/10"
                  title={t("avisos.scopeGlobalLabel")}
                >
                  <i className="ri-global-line mr-1"></i>
                  {t("avisos.allCompanies")}
                </span>
              )}
              {readedCount > 0 && (
                <span
                  className={`px-2 py-0.5 rounded-full ${themeClasses.textSecondary}`}
                  title={t("avisos.readedByTitle")}
                >
                  <i className="ri-eye-line mr-1"></i>
                  {readedCount}
                </span>
              )}
              {aviso.description && (
                <span
                  className={`hidden md:inline truncate ${themeClasses.textSecondary}`}
                  title={aviso.description}
                >
                  {aviso.description}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 flex-shrink-0">
          <span
            className={`${STATUS_CHIP} ${
              aviso.status === "active"
                ? "text-primary-500 bg-primary-500/10"
                : "text-gray-500 bg-gray-500/10"
            }`}
          >
            {aviso.status === "active"
              ? t("avisos.active")
              : t("avisos.inactive")}
          </span>

          <div className="flex items-center gap-0.5">
            <button
              onClick={() => onViewDetails(aviso)}
              aria-label={t("avisos.viewDetails")}
              className={`${ICON_BTN} ${themeClasses.buttonHover} ${themeClasses.buttonText} ${themeClasses.buttonTextHover}`}
              title={t("avisos.viewDetails")}
            >
              <i className="ri-information-line"></i>
            </button>
            <button
              onClick={() => onEdit(aviso)}
              aria-label={t("avisos.edit")}
              className={`${ICON_BTN} ${themeClasses.buttonHover} ${themeClasses.buttonText} ${themeClasses.buttonTextHover}`}
              title={t("avisos.edit")}
            >
              <i className="ri-edit-line"></i>
            </button>
            <button
              onClick={() => onToggleStatus(aviso)}
              disabled={isToggling}
              aria-label={
                aviso.status === "active"
                  ? t("avisos.deactivate")
                  : t("avisos.activate")
              }
              className={`${ICON_BTN} ${themeClasses.buttonHover} ${themeClasses.buttonText} ${themeClasses.buttonTextHover} disabled:opacity-50 disabled:cursor-not-allowed`}
              title={
                aviso.status === "active"
                  ? t("avisos.deactivate")
                  : t("avisos.activate")
              }
            >
              {isToggling ? (
                <i className="ri-loader-4-line animate-spin"></i>
              ) : (
                <i
                  className={
                    aviso.status === "active"
                      ? "ri-eye-off-line"
                      : "ri-eye-line"
                  }
                ></i>
              )}
            </button>
            <button
              onClick={() => onDelete(aviso)}
              className={ICON_BTN_DANGER}
              aria-label={t("avisos.delete")}
              title={t("avisos.delete")}
            >
              <i className="ri-delete-bin-line"></i>
            </button>
          </div>
        </div>

      {previewOpen && aviso.image && (
        <ImagePreviewModal
          src={aviso.image}
          alt={aviso.title || fallbackTitle}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </div>
  );
}
